import {
  CustomerLeadInput,
  CustomerLeadSchema,
  EmptyCustomerLeadError,
  InvalidCustomerLeadError,
  UpsertCustomerLeadInput,
} from "@yardzen/api-dtos";
import { AxiosError } from "axios";
import { z } from "zod";
import { apiClient } from "../base-client";

export async function findCustomerLead(yzAnonymousId: string) {
  try {
    const response = await apiClient.get(`customer-leads/${yzAnonymousId}`);
    if (response.data) {
      return validateCustomerLeadData(response.data);
    }

    return null;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      throw new EmptyCustomerLeadError();
    }
    throw error;
  }
}

export async function findCustomerLeadByAnonId(anonId: string) {
  try {
    const response = await apiClient.get(`customer-leads/${anonId}`);
    if (response.data) {
      return validateCustomerLeadData(response.data);
    }

    return null;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      throw new EmptyCustomerLeadError();
    }
    throw error;
  }
}

export async function checkIfCustomerEmailExists(email: string) {
  try {
    const response = await apiClient.get(`customer-leads/email/${email}`);
    // Response is an array of customer leads
    const customerLeads = response.data;

    if (!customerLeads || !Array.isArray(customerLeads)) {
      return [];
    }

    // Validate and return all customer leads
    const validatedLeads = customerLeads.map((lead: unknown) =>
      validateCustomerLeadData(lead),
    );

    return validatedLeads;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      // No customer leads found with this email
      return [];
    }
    console.error(
      `[checkIfCustomerEmailExists] Error checking customer leads for email: ${email}`,
      error,
    );
    throw error;
  }
}

export async function createCustomerLead(input: CustomerLeadInput) {
  try {
    const response = await apiClient.post("customer-leads", input);
    return validateCustomerLeadData(response.data);
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      throw new EmptyCustomerLeadError();
    }
    throw error;
  }
}

export async function updateCustomerLead(id: string, input: CustomerLeadInput) {
  try {
    const response = await apiClient.put(`customer-leads/${id}`, input);
    return validateCustomerLeadData(response.data);
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      throw new EmptyCustomerLeadError();
    }
    throw error;
  }
}

export async function upsertCustomerLead(
  yzAnonymousId: string,
  input: UpsertCustomerLeadInput,
) {
  try {
    const response = await apiClient.post(
      `customer-leads/${yzAnonymousId}/upsert`,
      input,
    );
    return validateCustomerLeadData(response.data);
  } catch (error) {
    console.error("upsertCustomerLead.error", error);
    if (error instanceof AxiosError && error.response?.status === 404) {
      throw new EmptyCustomerLeadError();
    }
    throw error;
  }
}

export async function sendNewLeadNotification(
  email: string,
  phone?: string,
  leadSource?: "pop-up" | "banner",
  isNewLead?: boolean,
) {
  try {
    const response = await apiClient.post(
      "customer-leads/send-new-lead-notification",
      {
        email,
        phone,
        leadSource,
        isNewLead,
      },
    );
    return response.data;
  } catch (error) {
    console.error("sendNewLeadNotification.error", error);
    throw error;
  }
}

function validateCustomerLeadData(data: unknown) {
  try {
    return CustomerLeadSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error(
        `Invalid customer lead data:`,
        JSON.stringify(data, null, 2),
      );
      throw new InvalidCustomerLeadError(error);
    }
    throw error;
  }
}
